import type { DashboardFeatureFlag, DashboardFeatureFlags } from '@dawn/feature-config';
import type { NavIconId } from '@dawn/ui';

/**
 * Canonical nav-item ids. Reference these (`NAV_IDS.overview`) instead of bare
 * string literals so call sites — active-state checks, the global-filter
 * allow-list — stay typo-safe and refactorable. Values are the route-stable
 * kebab ids; keys are camelCase for ergonomic access.
 */
export const NAV_IDS = {
  overview: 'overview',
  accessDiscovery: 'access-discovery',
  discoveryPrompt: 'discovery-prompt',
  accessPosture: 'access-posture',
  riskyIdentities: 'risky-identities',
  regulations: 'regulations',
  automatedReports: 'automated-reports',
  policies: 'policies',
  settings: 'settings',
} as const;

export type NavId = (typeof NAV_IDS)[keyof typeof NAV_IDS];

export interface NavItemConfig {
  id: NavId;
  label: string;
  iconId: NavIconId;
  path: string;
  enabled: boolean;
  /**
   * Key in the `featureFlags` object from GET /config. When set, the flag decides
   * `enabled` and the literal above is only the fallback for a deployment that
   * does not supply it. Typed: a key `DashboardFeatureFlagsSchema` does not
   * declare is a compile error, not an item that never opens.
   */
  featureFlag?: DashboardFeatureFlag;
}

export const PRIMARY_NAV_ITEMS: NavItemConfig[] = [
  {
    id: NAV_IDS.overview,
    label: 'Overview',
    iconId: 'grid2x2',
    path: '/',
    // Off for prod readiness (TDEV-949) — the Overview page is still WIP. The
    // flag turns it on per environment without moving the default route.
    enabled: false,
    featureFlag: 'overview',
  },
  {
    id: NAV_IDS.accessDiscovery,
    label: 'Access Discovery',
    iconId: 'lockX',
    path: '/access-discovery',
    enabled: true,
  },
  {
    id: NAV_IDS.discoveryPrompt,
    label: 'Discovery Prompt',
    iconId: 'monitorChecklist',
    path: '/discovery-prompt',
    // Disabled for prod readiness (TDEV-949) — WIP, shown as "Coming soon".
    enabled: false,
  },
  {
    id: NAV_IDS.accessPosture,
    label: 'Data Access Posture',
    iconId: 'key',
    path: '',
    enabled: false,
  },
  {
    id: NAV_IDS.riskyIdentities,
    label: 'Risky Identities',
    iconId: 'personWarning',
    path: '',
    enabled: false,
  },
  {
    id: NAV_IDS.regulations,
    label: 'Regulations & Compliance',
    iconId: 'documentShield',
    path: '',
    enabled: false,
  },
  {
    id: NAV_IDS.automatedReports,
    label: 'Automated Reports',
    iconId: 'cardBarsQuestion',
    path: '',
    enabled: false,
  },
  {
    id: NAV_IDS.policies,
    label: 'Policies',
    iconId: 'documentChecklist',
    path: '',
    enabled: false,
  },
];

export const FOOTER_NAV_ITEM: NavItemConfig = {
  id: NAV_IDS.settings,
  label: 'Settings',
  iconId: 'gear',
  path: '/settings',
  enabled: true,
};

/**
 * Applies the flags from GET /config over the declared defaults. Mirrors
 * resolveWidgets: the static array stays the source of order and labels, and the
 * deployment decides visibility.
 */
export function resolveNavItems(
  base: NavItemConfig[],
  featureFlags?: Partial<DashboardFeatureFlags>
): NavItemConfig[] {
  return base.map((item) => {
    if (item.featureFlag === undefined) return item;
    const flag = featureFlags?.[item.featureFlag];
    return flag === undefined ? item : { ...item, enabled: flag };
  });
}
